import { MessageSquare, MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { cn } from "../lib/utils";

export interface Conversation {
	id: string;
	title: string;
	createdAt: number;
	updatedAt: number;
	messageCount: number;
}

export function ConversationList({
	conversations,
	activeId,
	loading,
	onSelect,
	onNew,
	onDelete,
}: {
	conversations: Conversation[];
	activeId: string | null;
	loading: boolean;
	onSelect: (id: string) => void;
	onNew: () => void;
	onDelete: (id: string) => void;
}) {
	const [menuOpen, setMenuOpen] = useState<string | null>(null);

	const formatDate = (ts: number) => {
		const d = new Date(ts);
		return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
	};

	return (
		<div className="space-y-1">
			<div className="flex items-center justify-between px-3 py-1">
				<p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
					Conversations
				</p>
				<button
					type="button"
					onClick={onNew}
					className="p-1 rounded-md hover:bg-secondary transition-colors"
					title="New conversation"
				>
					<Plus className="w-3 h-3 text-muted-foreground" />
				</button>
			</div>

			{loading && conversations.length === 0 && (
				<div className="px-3 py-2 text-xs text-muted-foreground">Loading...</div>
			)}

			{conversations.length === 0 && !loading && (
				<div className="px-3 py-2 text-xs text-muted-foreground">
					No saved conversations yet. Start chatting and your conversations will appear here.
				</div>
			)}

			{conversations.map((conv) => (
				<div key={conv.id} className="relative px-1">
					<button
						type="button"
						onClick={() => onSelect(conv.id)}
						className={cn(
							"w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors group",
							activeId === conv.id
								? "bg-primary/10 text-primary"
								: "hover:bg-secondary text-foreground"
						)}
					>
						<MessageSquare className="w-3.5 h-3.5 shrink-0 opacity-60" />
						<span className="flex-1 text-left truncate text-xs">{conv.title}</span>
						<span className="text-[10px] text-muted-foreground shrink-0">
							{formatDate(conv.updatedAt)}
						</span>
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setMenuOpen(menuOpen === conv.id ? null : conv.id);
							}}
							className="p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-all"
						>
							<MoreHorizontal className="w-3 h-3" />
						</button>
					</button>

					{menuOpen === conv.id && (
						<div className="absolute right-1 top-7 z-20 bg-card border border-border rounded-md shadow-lg py-1 w-28">
							<button
								type="button"
								onClick={() => {
									if (window.confirm(`Delete "${conv.title}"?`)) {
										onDelete(conv.id);
									}
									setMenuOpen(null);
								}}
								className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10 transition-colors"
							>
								<Trash2 className="w-3 h-3" />
								Delete
							</button>
						</div>
					)}
				</div>
			))}
		</div>
	);
}
